import { useRef, useState, useEffect, useCallback } from 'react';
import { useNotifier } from '@provider/notifier';
import { getAbsoluteUrlFromSrc } from '../utils/get-absolute-url-from-src.js';

/**
 * begin playback of the specified url and return controls to render an audio
 * player interface.
 */
function useAudio({
  src,
  onEnd,
  onError
}) {
  const notifier = useNotifier();
  const audioRef = useRef(undefined);
  const durationMsRef = useRef(undefined);
  const [isPlaying, setPlaying] = useState(true);
  const [timeElapsedMs, setTimeElapsedMs] = useState(undefined);
  useEffect(() => {
    let isActiveSrc = true;
    let animationFrame;
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("play", () => setPlaying(true));
    document.body.appendChild(audio);

    // cache reference to the playback element, so we can reference it later
    // on in the control methods that are exposed publicly
    audioRef.current = audio;
    getAbsoluteUrlFromSrc(src).then(url => {
      if (!isActiveSrc) {
        // bail out if the active audio src has been changed since we
        // requested the absolute url
        return;
      }
      if (!url) {
        throw new Error("useAudoi(): url missing");
      }
      audio.src = url;

      // begin playback instantly
      audio.play().catch(err => {
        notifier.notify({
          id: src,
          type: "error",
          body: "Lydfilen kunne ikke afspilles. Prøv igen senere."
        });
        console.error(err);
        onError?.();
      });

      // reset internal state so we reflect that a new file is currently
      // being played
      durationMsRef.current = undefined;
      setTimeElapsedMs(undefined);

      // subscribe to progress using an animation frame loop while the audio
      // is still playing
      const onProgress = () => {
        if (audio.duration) {
          durationMsRef.current = audio.duration * 1000;
        }
        if (audio.currentTime) {
          setTimeElapsedMs(audio.currentTime * 1000);
        }
        animationFrame = requestAnimationFrame(onProgress);
      };
      animationFrame = requestAnimationFrame(onProgress);
    }).catch(err => {
      notifier.notify({
        id: src,
        type: "error",
        body: "Lydfilen kunne ikke afspilles. Prøv igen senere."
      });
      console.error(err);
      onError?.();
    });
    return () => {
      // register that this src is no longer actively requested
      isActiveSrc = false;

      // stop playback when the component is unmounted
      audio.pause();
      audio.remove();

      // stop tracking progress once the component has been unmounted
      cancelAnimationFrame(animationFrame);
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [src]);

  // bind event listeners to ensure that audio progress is going to be emitted
  // properly
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const handleEnd = () => {
      setPlaying(false);
      setTimeElapsedMs(0);
      audio.currentTime = 0;
      if (onEnd) {
        onEnd();
      }
    };
    audio.addEventListener("ended", handleEnd);
    return () => {
      audio.removeEventListener("ended", handleEnd);
    };
  }, [src, onEnd]);
  return {
    resume: useCallback(() => {
      audioRef.current?.play();
      setPlaying(true);
    }, []),
    pause: useCallback(() => {
      audioRef.current?.pause();
      setPlaying(false);
    }, []),
    seek: useCallback(timeMs => {
      if (audioRef.current) {
        audioRef.current.currentTime = timeMs;
      }
    }, []),
    isPlaying,
    durationMs: durationMsRef.current,
    timeElapsedMs: timeElapsedMs
  };
}

export { useAudio };
//# sourceMappingURL=use-audio.js.map
